import { useState, useEffect } from "react";
import { T } from "@freightsimple/ui-core/Typography/T";
import { BOOKING_TIPS } from "../Helpers/bookShipmentTips";

interface TipsCarouselProps {
  autoRotateInterval?: number | undefined;
}

export function BookShipmentTipsCarousel({
  autoRotateInterval = 4000,
}: TipsCarouselProps) {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const handleDotClick = (index: number) => {
    if (index === currentTipIndex || isExiting) return;

    setIsPaused(true);

    setIsExiting(true);

    setTimeout(() => {
      setCurrentTipIndex(index);
      setIsExiting(false);

      setTimeout(() => {
        setIsPaused(false);
      }, 8000);
    }, 250);
  };

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      if (!isPaused) {
        setIsExiting(true);

        setTimeout(() => {
          setCurrentTipIndex((prev) => (prev + 1) % BOOKING_TIPS.length);
          setIsExiting(false);
        }, 250);
      }
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [autoRotateInterval, isPaused]);

  const currentTip = BOOKING_TIPS[currentTipIndex];

  if (currentTip === undefined) {
    return <></>;
  }

  return (
    <div className="relative min-h-[155px] w-full overflow-hidden rounded-xl border border-border bg-card p-5 shadow-[0_0_5px_0_var(--color-shadow-ambient)]">
      <div
        key={currentTipIndex}
        className={`absolute inset-5 flex flex-col gap-3 ${
          isExiting ? "animate-tip-out" : "animate-tip-in"
        }`}
      >
        <div className="flex items-center gap-2">
          <T weight="semibold">{currentTip.title}</T>
        </div>
        <T size="sm">{currentTip.description}</T>
        <a
          className="text-[13px] font-medium text-brand-text no-underline hover:underline"
          href={currentTip.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more →
        </a>
      </div>

      <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
        {BOOKING_TIPS.map((tip, index) => (
          <button
            type="button"
            aria-label={`Show tip: ${tip.title}`}
            key={tip.title}
            className={`h-1.5 w-1.5 cursor-pointer rounded-full border-0 p-0 transition-all duration-300 ease-[ease] hover:scale-125 hover:bg-brand ${
              index === currentTipIndex ? "bg-brand" : "bg-border-strong"
            }`}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </div>
    </div>
  );
}
